import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Globe, Linkedin, Building2, User, Eye, Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import AppLayout from "../components/AppLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Tag({ color = "var(--brand-teal)", children }) {
  return (
    <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
      style={{ color, backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}>{children}</span>
  );
}

export default function ProfilePreview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/hub/preview`, { withCredentials: true });
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) {
    return <AppLayout><div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div></AppLayout>;
  }

  const tier = (data.membership_tier || "free");
  const showBadge = tier !== "free"; // no fake Verified for Free

  return (
    <AppLayout>
      <Seo title="Profile preview" path="/app/profile/preview" />
      <div className="container-hub py-8 lg:py-12" data-testid="profile-preview-page">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/app/profile" className="mb-1 inline-flex items-center gap-1 text-xs font-medium text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"><ArrowLeft className="h-3.5 w-3.5" /> Back to My profile</Link>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)]">Profile preview</h1>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-teal)]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-teal)]"><Eye className="h-3 w-3" /> Preview</span>
            </div>
            <p className="mt-1 text-sm text-[color:var(--ink-soft)]">This is roughly how your profile will appear when the public Hub directory launches.</p>
          </div>
        </div>

        <div className="hub-card overflow-hidden rounded-3xl">
          <div className="brand-bar h-2" />
          <div className="p-8 lg:p-10">
            <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-full border border-[color:var(--hairline)] bg-[color:var(--soft)]">
                {data.profile_photo ? <img src={data.profile_photo} alt="" className="h-full w-full object-cover" /> :
                  <div className="flex h-full w-full items-center justify-center text-[color:var(--ink-soft)]"><User className="h-8 w-8" /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-display text-2xl font-semibold text-[color:var(--ink)]" data-testid="pv-name">{data.name || "Member"}</h2>
                  {showBadge && <Tag color="var(--brand-red)">Verified</Tag>}
                  {data.member_type && <Tag color="var(--brand-teal)">{data.member_type}</Tag>}
                </div>
                {data.professional_headline && <p className="mt-2 text-[color:var(--ink)]">{data.professional_headline}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[color:var(--ink-soft)]">
                  {(data.city || data.country) && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[data.city, data.country].filter(Boolean).join(", ")}</span>}
                  {data.website && <a href={data.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[color:var(--ink)]"><Globe className="h-3.5 w-3.5" />Website</a>}
                  {data.linkedin_url && <a href={data.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[color:var(--ink)]"><Linkedin className="h-3.5 w-3.5" />LinkedIn</a>}
                </div>
              </div>
            </div>

            {data.bio && (<div className="mt-8 border-t border-[color:var(--hairline)] pt-6"><p className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">About</p><p className="mt-3 text-[color:var(--ink)]">{data.bio}</p></div>)}

            {data.business && (
              <div className="mt-8 border-t border-[color:var(--hairline)] pt-6" data-testid="pv-business">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">Business</p>
                <div className="mt-4 flex items-start gap-4">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--soft)]">
                    {data.business.logo ? <img src={data.business.logo} alt="" className="h-full w-full object-cover" /> :
                      <div className="flex h-full w-full items-center justify-center text-[color:var(--ink-soft)]"><Building2 className="h-6 w-6" /></div>}
                  </div>
                  <div>
                    <p className="font-display text-lg font-semibold text-[color:var(--ink)]">{data.business.name || "—"}</p>
                    <p className="text-sm text-[color:var(--ink-soft)]">{[data.business.industry, data.business.stage, [data.business.city, data.business.country].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</p>
                    {data.business.description && <p className="mt-3 text-sm text-[color:var(--ink)]">{data.business.description}</p>}
                    {data.business.products_services && <p className="mt-2 text-sm text-[color:var(--ink-soft)]">{data.business.products_services}</p>}
                    {(data.business.markets || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <span className="text-xs text-[color:var(--ink-soft)]">Markets:</span>
                        {data.business.markets.map((m) => <span key={m} className="rounded-full bg-[color:var(--soft)] px-2 py-0.5 text-xs">{m}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(data.offers?.length > 0) && (
              <div className="mt-8 border-t border-[color:var(--hairline)] pt-6" data-testid="pv-offers">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">What I offer</p>
                <div className="mt-3 flex flex-wrap gap-2">{data.offers.map((o) => <Tag key={o} color="var(--brand-green)">{o}</Tag>)}</div>
              </div>
            )}

            {(data.looking_for?.length > 0 || data.looking_for_details) && (
              <div className="mt-8 border-t border-[color:var(--hairline)] pt-6" data-testid="pv-looking">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">What I'm looking for</p>
                <div className="mt-3 flex flex-wrap gap-2">{(data.looking_for || []).map((o) => <Tag key={o} color="var(--brand-orange)">{o}</Tag>)}</div>
                {data.looking_for_details && <p className="mt-3 text-sm text-[color:var(--ink)]">{data.looking_for_details}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
